/**
 * Where the audience is lost — the visibility funnel, the client against a
 * comparator of the reader's choosing (2026-09-08).
 *
 * THE ARGUMENT: a count of "10 answers" is a quantity. A funnel shows WHERE the
 * loss happens, which is the only part a client can act on. Antibodies Inc reads
 * 104 -> 10 -> 5: ninety percent is lost at the first step and half is retained
 * at the second. Side by side with a comparator the argument makes itself.
 *
 * THE COMPARATOR (Lisa, 2026-09-08). The first version always drew the category
 * leader beside the client — Thermo Fisher at 68 next to Antibodies Inc at 10.
 * "This is a perfect example of you versus the top and nothing more ... it's
 * just rubbing it in the face. Thermo wasn't one of their named competitors."
 * So the funnel now opens on the strongest NAMED competitor — where this client
 * leads on every step — and the leaders sit in the same control, which is her
 * "if you changed it to Abcam, and then you change it to Vector" made literal.
 * The first band also says what it is ("26 prompts × 4 models"), because her
 * first question on seeing it was "is 104 the prompts?"
 *
 * REBUILT after Ramos: "this is not intuitive or obvious what you are visually
 * representing." Bands are CONTIGUOUS trapezoids so the taper is the message;
 * stage labels share a left-hand column so the two companies line up row for
 * row; counts sit outside a band too thin to hold them.
 *
 * Only steps that genuinely NEST are drawn. Mention / Citation / Recommendation
 * overlap and do not nest (2026-07-13 D1), so they are not funnel stages;
 * Visibility does contain Recommendation, so those two are.
 */
import type {
  CompanyFunnel,
  FunnelComparators,
  FunnelContext,
} from '@aurora/shared';
import { CONTEXT, TARGET } from './palette';

const W = 250;
const BAND_H = 62;
/** Minimum drawn width, so a near-zero step is still a visible shape. */
const MIN_W = 10;

const pct = (v: number): string => {
  const p = v * 100;
  if (p > 0 && p < 1) return '<1%';
  return `${Math.round(p)}%`;
};

function FunnelShape({ f }: { f: CompanyFunnel }): JSX.Element {
  const hue = f.isTarget ? TARGET : CONTEXT;
  const first = Math.max(1, f.steps[0]?.value ?? 1);
  const widthAt = (i: number): number => {
    const v = f.steps[i]?.value ?? 0;
    return Math.max(MIN_W, (v / first) * W);
  };
  const h = f.steps.length * BAND_H;

  return (
    <svg
      width={W}
      height={h}
      viewBox={`0 0 ${W} ${h}`}
      preserveAspectRatio="xMidYMin meet"
      role="img"
      aria-label={`${f.company}: ${f.steps
        .map((s) => `${s.label} ${s.value}`)
        .join(', ')}`}
      className="funnel-svg"
    >
      {f.steps.map((s, i) => {
        const yTop = i * BAND_H;
        const wTop = widthAt(i);
        // The last band keeps its width (a rectangle); every other band tapers
        // into the next step, which is what makes the drop visible as geometry.
        const wBot = i === f.steps.length - 1 ? wTop : widthAt(i + 1);
        const x1 = (W - wTop) / 2;
        const x2 = (W - wBot) / 2;
        const pts = [
          `${x1},${yTop}`,
          `${x1 + wTop},${yTop}`,
          `${x2 + wBot},${yTop + BAND_H}`,
          `${x2},${yTop + BAND_H}`,
        ].join(' ');
        const wMid = (wTop + wBot) / 2;
        const roomInside = wMid > 54;
        return (
          <g key={s.label}>
            <polygon
              points={pts}
              fill={hue}
              opacity={i === 0 ? 0.32 : i === 1 ? 0.62 : 1}
              stroke="#0b1020"
              strokeWidth={1}
            >
              <title>
                {f.company} — {s.label}: {s.value} ({pct(s.ofTotal)} of all
                answers)
              </title>
            </polygon>
            <text
              x={roomInside ? W / 2 : W / 2 + wMid / 2 + 12}
              y={yTop + BAND_H / 2}
              textAnchor={roomInside ? 'middle' : 'start'}
              dominantBaseline="middle"
              className={roomInside ? 'funnel-value' : 'funnel-value is-outside'}
            >
              {s.value}
            </text>
            {i < f.steps.length - 1 &&
              (() => {
                const next = f.steps[i + 1];
                if (!next || next.retained === null) return null;
                return (
                  <text
                    x={2}
                    y={yTop + BAND_H - 4}
                    dominantBaseline="middle"
                    className={
                      i + 1 === f.biggestDropAt
                        ? 'funnel-drop is-worst'
                        : 'funnel-drop'
                    }
                  >
                    ↓ {pct(1 - next.retained)} lost
                  </text>
                );
              })()}
          </g>
        );
      })}
    </svg>
  );
}

export function VisibilityFunnel({
  funnels,
  comparators,
  context,
  verdict,
  selected,
  onSelect,
}: {
  funnels: CompanyFunnel[];
  comparators: FunnelComparators;
  context: FunnelContext | null;
  verdict: string | null;
  /** The comparator currently drawn (null = the derivation's default). */
  selected: string | null;
  onSelect: (company: string) => void;
}): JSX.Element | null {
  if (funnels.length === 0) return null;
  const steps = funnels[0]?.steps ?? [];
  const named = comparators.options.filter((o) => o.group === 'named');
  const leaders = comparators.options.filter((o) => o.group === 'leader');
  const answers = (n: number): string => (n === 1 ? 'answer' : 'answers');

  return (
    <div className="funnel-wrap">
      {comparators.options.length > 1 && (
        <div className="funnel-controls">
          <label className="sov-filter">
            <span className="muted">Compare against</span>
            <select
              value={selected ?? comparators.defaultCompany ?? ''}
              onChange={(e) => onSelect(e.target.value)}
            >
              {named.length > 0 && (
                <optgroup label="Your named competitors">
                  {named.map((o) => (
                    <option key={o.company} value={o.company}>
                      {o.company} · {o.visible} {answers(o.visible)}
                    </option>
                  ))}
                </optgroup>
              )}
              {leaders.length > 0 && (
                <optgroup label="Category leaders AI surfaced">
                  {leaders.map((o) => (
                    <option key={o.company} value={o.company}>
                      {o.company} · {o.visible} {answers(o.visible)}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>
        </div>
      )}
      <p className="funnel-lede">
        Of every AI answer in this benchmark, how many reached each company — and
        how many went on to recommend it. Each band narrows into the next, so the
        drop is the shape.
      </p>
      <div
        className="funnel-grid"
        style={{ gridTemplateColumns: `168px repeat(${funnels.length}, 1fr)` }}
      >
        <div />
        {funnels.map((f) => (
          <div
            key={f.company}
            className={f.isTarget ? 'funnel-head is-target' : 'funnel-head'}
          >
            {f.company}
            {f.isTarget ? ' (you)' : ''}
            {!f.isTarget && (
              <span className="funnel-head-role">
                {f.tracked ? 'named competitor' : 'category leader AI surfaced'}
              </span>
            )}
          </div>
        ))}

        {/* Stage labels shared down the left, so both columns align row by row. */}
        <div className="funnel-steps">
          {steps.map((s, i) => (
            <div key={s.label} className="funnel-step-row">
              <span className="funnel-step-name">{s.label}</span>
              {i === 0 && context && context.models > 0 && (
                <span className="funnel-step-sub">
                  {context.prompts} prompts × {context.models} AI models
                </span>
              )}
            </div>
          ))}
        </div>

        {funnels.map((f) => (
          <div key={f.company} className="funnel-col">
            <FunnelShape f={f} />
          </div>
        ))}
      </div>
      {verdict && <p className="chart-verdict">{verdict}</p>}
    </div>
  );
}
